import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  Grid,
  Tooltip,
  IconButton,
  Divider,
  TableFooter,
  Collapse,
} from "@mui/material";
import {
  VerticalAlignTop as VerticalAlignTopIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as CheckIcon,
  AccountBalanceWallet as WalletIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";

import { UrlApi } from "../../../../../../../../services/apirest";
import { useToast } from "../../../../../../../../Hooks/UseToast";

/**
 * ImputationModal -> importante leer ;)
 *
 *
 * Soporta DOS modos según `mode`:
 *
 *   "inline"  → Se usa DENTRO del ReceiptDialog (antes de guardar el recibo).
 *               Escribe en el estado local del recibo vía callbacks del reducer.
 *               No hace fetch a la API al guardar: los datos se envían junto con el recibo.
 *               Props requeridas en este modo:
 *                 - imputaciones       array   state.imputaciones del reducer
 *                 - saldoDisponible    number  totalCobrado calculado en ese momento
 *                 - onAdd, onUpdate, onRemove  callbacks del UseReceipt hook
 *
 *   "post"    → Se usa DESPUÉS de guardar (ej: desde ReciboDetalleDialog o una lista).
 *               Hace fetch a GET /recibos/facturas/saldo/:cuit para obtener facturas
 *               y POST /recibos/imputar al confirmar.
 *               Props requeridas en este modo:
 *                 - recibo     { id, nro_recibo, saldo, fecha_de_pago }
 *                 - cuit       string
 *                 - user       string
 *                 - onSuccess  () => void
 *
 * Props comunes:
 *   open          boolean
 *   onClose       () => void
 *   mode          "inline" | "post"
 *   clienteData   { razon_social, ... }
 */
export default function ImputacionModal({
  open,
  onClose,
  mode = "post",
  clienteData,
  getDocuments,

  // Props modo "inline"
  imputaciones: imputacionesExternas = [],
  saldoDisponible = 0,
  onAdd,
  onUpdate,
  onRemove,
  onSetImputaciones,

  // Props modo "post"
  recibo,
  cuit,
  user,
  onSuccess,
}) {
  // console.log(`Recibo seleccionado: `, recibo);

  const idCliente = clienteData.id;
  const idTipoNegocio =
    clienteData.id_negocio_cliente ?? clienteData.id_tipocliente;
  console.log("IdTipoDeNegocio: ", idTipoNegocio);

  const [recibos, setRecibos] = useState([]);
  const [recibosExpanded, setRecibosExpanded] = useState(false);
  // ── Facturas (se cargan en ambos modos) ──────────────────────────────────
  const [facturas, setFacturas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [errorFacturas, setErrorFacturas] = useState(null);

  // ── Estado local de montos (Map id_factura → importe) ───────────────────
  // En modo "inline" se sincroniza con el reducer externo.
  // En modo "post" es completamente local hasta que se confirma.
  const [montos, setMontos] = useState({});
  const [montosIniciales, setMontosIniciales] = useState({});

  // ── Guardado (solo modo "post") ──────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveOk, setSaveOk] = useState(false);

  const cuitEfectivo = cuit || clienteData?.cuit || "";

  const { Toast, showToast } = useToast();

  useEffect(() => {
    if (!open) return;

    setRecibos([]);

    fetch(`${UrlApi}/ctacte/recibos/saldo/${idCliente}/${idTipoNegocio}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => {
        if (data?.error) throw new Error(data.error);
        // console.log("Recibos: ", data);
        const recibosFiltrados = data?.filter(
          (d) => d.nro_recibo !== recibo?.nro_recibo,
        );
        setRecibos(
          Array.isArray(recibosFiltrados)
            ? recibosFiltrados
            : Array.isArray(recibos)
              ? recibos
              : [],
        );
      });
  }, [open, idCliente, idTipoNegocio, recibo]);

  useEffect(() => {
    if (!open) return;

    setLoadingFacturas(true);
    setErrorFacturas(null);
    setMontos({});
    setSaveError(null);
    setSaveOk(false);

    fetch(
      `${UrlApi}/ctacte/recibos/facturas/saldo/${idCliente}/${idTipoNegocio === 3 ? 1 : 0}`,
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => {
        if (data?.error) throw new Error(data.error);
        const lista = Array.isArray(data) ? data : [];
        setFacturas(lista);

        // Inicializar montos
        if (mode === "inline") {
          // Si el reducer ya tiene imputaciones, las cargamos
          const init = {};
          lista.forEach((f) => {
            const imp = imputacionesExternas.find(
              (i) => i.id_factura === f.id_factura,
            );
            init[f.id_factura] = imp ? Number(imp.importe) : 0;
          });
          setMontos(init);
          setMontosIniciales({ ...init });
        } else {
          const init = {};
          lista.forEach((f) => {
            init[f.id_factura] = 0;
          });
          setMontos(init);
          setMontosIniciales({ ...init });
        }
      })
      .catch((err) => setErrorFacturas(err.message))
      .finally(() => setLoadingFacturas(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cuitEfectivo]);

  const saldoEfectivo =
    mode === "inline" ? saldoDisponible : parseFloat(recibo?.saldo ?? 0);

  const totalImputado = useMemo(
    () =>
      Object.values(montos).reduce((acc, v) => acc + (parseFloat(v) || 0), 0),
    [montos],
  );
  const saldoRestante = saldoEfectivo - totalImputado;
  const porcentaje =
    saldoEfectivo > 0 ? (totalImputado / saldoEfectivo) * 100 : 0;

  const handleChangeBusqueda = (e) => {
    const value = e.target.value.replace(/[^0-9-]/g, "");
    setBusqueda(value);
  };

  const setMonto = (idFactura, valor) => {
    const factura = facturas.find((f) => f.id_factura === idFactura);
    if (!factura) return;

    const montoActual = parseFloat(montos[idFactura]) || 0;
    const saldoDisp = saldoRestante + montoActual;
    const max = Math.min(parseFloat(factura.saldo), saldoDisp);
    const final = Math.max(0, Math.min(parseFloat(valor) || 0, max));

    setMontos((prev) => ({ ...prev, [idFactura]: final }));
  };

  const ajustar = (idFactura, delta) => {
    setMonto(idFactura, (parseFloat(montos[idFactura]) || 0) + delta);
  };

  const huboCambios = useMemo(
    () =>
      Object.keys(montos).some(
        (id) =>
          (parseFloat(montos[id]) || 0) !==
          (parseFloat(montosIniciales[id]) || 0),
      ),
    [montos, montosIniciales],
  );

  const imputarMaximo = (idFactura) => {
    const factura = facturas.find((f) => f.id_factura === idFactura);
    if (!factura) return;
    const montoActual = parseFloat(montos[idFactura]) || 0;
    const max = Math.min(
      parseFloat(factura.saldo),
      saldoRestante + montoActual,
    );
    setMontos((prev) => ({ ...prev, [idFactura]: max }));
  };

  const handleGuardar = async () => {
    const imputacionesActivas = Object.entries(montos)
      .filter(([, imp]) => parseFloat(imp) > 0)
      .map(([id_factura, importe]) => ({
        id_factura: parseInt(id_factura, 10),
        importe: parseFloat(importe),
      }));

    if (mode === "inline") {
      const imputacionesActivas = Object.entries(montos)
        .filter(([, imp]) => parseFloat(imp) > 0)
        .map(([id_factura, importe]) => ({
          id_factura: parseInt(id_factura, 10),
          importe: parseFloat(importe),
          user: localStorage.getItem("user"),
        }));

      onSetImputaciones(imputacionesActivas);
      onClose();
      return;
    }

    // Modo "post" → POST a la API
    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch(`${UrlApi}/ctacte/recibos/imputar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idRecibo: recibo.id,
          imputaciones: imputacionesActivas,
          user,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);

      showToast(
        `${imputacionesActivas.length > 1 ? "Las aplicaciones fueron creadas con exito" : "Se aplico correctamente el recibo"}`,
        "success",
      );
      setSaveOk(true);
      getDocuments();

      setTimeout(() => {
        setSaveOk(false);
        onClose();
      }, 1200);
    } catch (err) {
      setSaveError(err.message);
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setMontos({});
    setSaveError(null);
    setSaveOk(false);
    onClose();
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
    }).format(amount ?? 0);

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "-";
    const d = new Date(fechaStr);
    return isNaN(d)
      ? fechaStr
      : d.toLocaleDateString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  // console.log("Facturas: ", facturas);
  const facturasFiltradas = useMemo(() => {
    return facturas.filter((f) =>
      String(f.nro_comprobante).startsWith(busqueda),
    );
  }, [facturas, busqueda]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      {/* ── Título ── */}
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          py: 2,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <WalletIcon sx={{ fontSize: 30 }} />
            <Box>
              <Box display="flex" alignItems="baseline" gap={1.2}>
                <Typography variant="h6" fontWeight="bold">
                  Aplicar Recibo
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontWeight: 400,
                  }}
                >
                  • Solo se muestran facturas con saldo pendiente. Ajustá los
                  montos a aplicar.
                </Typography>
              </Box>
              {clienteData?.razon_social && (
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {clienteData.razon_social}
                </Typography>
              )}
            </Box>
          </Box>

          {mode === "post" && recibo && (
            <Chip
              label={`Recibo ${recibo.nro_recibo} · ${formatFecha(recibo.fecha_de_pago)}`}
              sx={{ bgcolor: "white", color: "primary.main", fontWeight: 700 }}
            />
          )}

          {mode === "inline" && (
            <Chip
              label="Aplicacion al crear el recibo"
              sx={{ bgcolor: "white", color: "primary.main", fontWeight: 700 }}
            />
          )}
        </Box>

        {/* Cards de resumen */}
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <Paper sx={{ p: 1, textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, fontWeight: 700 }}
                >
                  SALDO
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {formatCurrency(saldoEfectivo)}
                </Typography>
                <Box sx={{ mt: 0.7 }}></Box>
              </Paper>
            </Grid>

            <Grid item xs={3}>
              <Paper sx={{ p: 1, textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, fontWeight: 700 }}
                >
                  IMPUTADO
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {formatCurrency(totalImputado)}
                </Typography>
                <Box sx={{ mt: 0.7 }}></Box>
              </Paper>
            </Grid>

            <Grid item xs={3}>
              <Paper
                sx={{
                  p: 1,
                  textAlign: "center",
                  bgcolor: saldoRestante === 0 ? "success.50" : "warning.50",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, fontWeight: 700 }}
                >
                  RESTANTE
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {formatCurrency(saldoRestante)}
                </Typography>
                <Box sx={{ mt: 0.7 }}></Box>
              </Paper>
            </Grid>

            <Grid item xs={3}>
              <Paper sx={{ p: 1, textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, fontWeight: 700 }}
                >
                  USADO
                </Typography>

                <Typography variant="body1" fontWeight={700}>
                  {porcentaje.toFixed(1)}%
                </Typography>

                <Box
                  sx={{
                    mt: 0.3,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "grey.200",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${Math.min(porcentaje, 100)}%`,
                      bgcolor: "primary.main",
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Divider />

        {/* ── Cuerpo ── */}
        <Box sx={{ p: 3 }}>
          {/* Cargando */}
          {loadingFacturas && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={6}
              gap={2}
            >
              <CircularProgress size={28} />
              <Typography color="text.secondary">
                Cargando facturas con saldo pendiente…
              </Typography>
            </Box>
          )}

          {/* Error */}
          {!loadingFacturas && errorFacturas && (
            <Alert severity="error" icon={<ErrorIcon />}>
              <Typography fontWeight="bold">
                No se pudieron cargar las facturas
              </Typography>
              <Typography variant="body2">{errorFacturas}</Typography>
            </Alert>
          )}

          {/* ── Recibos pendientes de aplicar ── */}
          {recibos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                onClick={() => setRecibosExpanded((v) => !v)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  px: 2,
                  py: 1.2,
                  bgcolor: "primary.50",
                  borderRadius: recibosExpanded ? "8px 8px 0 0" : 2,
                  border: "1px solid",
                  borderColor: "primary.200",
                  userSelect: "none",
                  "&:hover": { bgcolor: "primary.100" },
                  transition: "background-color 0.15s",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <ReceiptLongIcon
                    sx={{ color: "primary.main", fontSize: 20 }}
                  />
                  <Typography
                    fontWeight={700}
                    color="primary.main"
                    variant="body2"
                  >
                    Recibos con saldo disponible
                  </Typography>
                  <Chip
                    label={`${recibos.length} recibo${recibos.length > 1 ? "s" : ""}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: 11 }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="primary.main"
                  >
                    Total disponible:{" "}
                    {formatCurrency(
                      recibos.reduce(
                        (acc, r) => acc + parseFloat(r.saldo ?? 0),
                        0,
                      ),
                    )}
                  </Typography>
                  {recibosExpanded ? (
                    <KeyboardArrowUpIcon sx={{ color: "primary.main" }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ color: "primary.main" }} />
                  )}
                </Box>
              </Box>

              <Collapse in={recibosExpanded}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderTop: "none", borderRadius: "0 0 8px 8px" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "primary.50" }}>
                        <TableCell
                          sx={{ fontWeight: 700, fontSize: 12, width: "20%" }}
                        >
                          Nro Recibo
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, fontSize: 12, width: "20%" }}
                        >
                          Fecha
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: 12, width: "20%" }}
                        >
                          Total Recibo
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: 12, width: "20%" }}
                        >
                          Ya aplicado
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, fontSize: 12, width: "20%" }}
                        >
                          Saldo disponible
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recibos.map((r) => (
                        <TableRow
                          key={r.id}
                          sx={{ "&:hover": { bgcolor: "grey.50" } }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontFamily="monospace"
                              fontWeight={600}
                            >
                              {r.nro_recibo}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatFecha(r.fecha_de_pago)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(r.total)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatCurrency(r.total - r.saldo)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatCurrency(r.saldo)}
                              size="small"
                              color={
                                parseFloat(r.saldo) > 0 ? "success" : "default"
                              }
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell
                          colSpan={4}
                          sx={{ fontWeight: 700, fontSize: 12 }}
                        >
                          TOTAL DISPONIBLE PARA APLICAR
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={formatCurrency(
                              recibos.reduce(
                                (acc, r) => acc + parseFloat(r.saldo ?? 0),
                                0,
                              ),
                            )}
                            color="success"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>
          )}

          {/* Tabla Facturas */}
          {!loadingFacturas && !errorFacturas && facturas.length > 0 && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="body2" color="text.secondary">
                  Seleccioná las facturas a las que querés aplicar el saldo.
                </Typography>

                <TextField
                  sx={{ width: "265px" }}
                  size="small"
                  placeholder="Buscar por N° de comprobante..."
                  value={busqueda}
                  onChange={handleChangeBusqueda}
                />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Comprobante
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Nro</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Emisión</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Vencimiento
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Imputado
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Saldo
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, minWidth: 280 }}
                      >
                        Monto a Aplicar
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facturasFiltradas?.map((factura) => {
                      const montoActual =
                        parseFloat(montos[factura.id_factura]) || 0;
                      const saldoDisp = saldoRestante + montoActual;
                      const maxDisponible = Math.min(
                        parseFloat(factura.saldo),
                        saldoDisp,
                      );
                      const estaImputada = montoActual > 0;

                      return (
                        <TableRow
                          key={factura.id_factura}
                          sx={{
                            bgcolor: estaImputada ? "info.50" : "inherit",
                            "&:hover": {
                              bgcolor: estaImputada ? "info.100" : "grey.50",
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {factura.texto_comprobante}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontFamily="monospace"
                              color="text.secondary"
                            >
                              {factura.nro_comprobante}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatFecha(factura.fecha_de_pago)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatFecha(factura.fecha_vencimiento)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(factura.total)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {formatCurrency(factura.imputado)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatCurrency(factura.saldo)}
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              justifyContent="flex-end"
                            >
                              <Tooltip title="Restar $1.000">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      ajustar(factura.id_factura, -1000)
                                    }
                                    disabled={montoActual === 0}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <TextField
                                size="small"
                                type="number"
                                value={montoActual === 0 ? "" : montoActual}
                                onChange={(e) =>
                                  setMonto(factura.id_factura, e.target.value)
                                }
                                onWheel={(e) => e.target.blur()}
                                placeholder="0,00"
                                inputProps={{
                                  min: 0,
                                  max: maxDisponible,
                                  step: 100,
                                  style: { textAlign: "right", width: 110 },
                                }}
                                sx={{
                                  width: 130,
                                  "& input::-webkit-outer-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "& input::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "& input[type=number]": {
                                    MozAppearance: "textfield",
                                  },
                                }}
                              />

                              <Tooltip title="Sumar $1.000">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      ajustar(factura.id_factura, 1000)
                                    }
                                    disabled={montoActual >= maxDisponible}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              <Tooltip title="Aplicar máximo disponible">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      imputarMaximo(factura.id_factura)
                                    }
                                    disabled={montoActual >= maxDisponible}
                                    color="primary"
                                  >
                                    <VerticalAlignTopIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {!loadingFacturas &&
            !errorFacturas &&
            facturasFiltradas.length === 0 && (
              <Box
                sx={{
                  border: "0.5px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  textAlign: "center",
                }}
              >
                {/* Ícono central */}
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    border: "0.5px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WalletIcon sx={{ fontSize: 32, color: "text.disabled" }} />
                </Box>

                {/* Título */}
                <Typography variant="h6" fontWeight={500} color="text.primary">
                  {busqueda
                    ? `Sin facturas para "${busqueda}"`
                    : "No hay facturas con saldo pendiente"}
                </Typography>

                {/* Subtítulo dinámico */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 380, lineHeight: 1.7 }}
                >
                  {busqueda
                    ? "No hay comprobantes con ese número que tengan saldo pendiente. Probá con otro número o limpiá la búsqueda."
                    : "Este cliente no tiene facturas pendientes de aplicar en este momento."}
                </Typography>

                {/* Chips informativos */}
                <Box
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  justifyContent="center"
                  mt={0.5}
                >
                  <Chip
                    size="small"
                    icon={<CheckIcon sx={{ fontSize: 14 }} />}
                    label="Solo se muestran facturas con saldo > 0"
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                  {busqueda && (
                    <Chip
                      size="small"
                      label="Búsqueda por número de comprobante"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  )}
                </Box>

                {/* Botón limpiar (solo cuando hay búsqueda activa) */}
                {busqueda && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setBusqueda("")}
                    sx={{ mt: 0.5, textTransform: "none" }}
                  >
                    Limpiar búsqueda
                  </Button>
                )}
              </Box>
            )}

          {/* Error al guardar */}
          {saveError && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 3 }}>
              {saveError}
            </Alert>
          )}

          {/* Éxito */}
          {saveOk && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mt: 3 }}>
              Imputación guardada correctamente.
            </Alert>
          )}
        </Box>
      </DialogContent>

      {/* ── Acciones ── */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button variant="outlined" onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleGuardar}
          disabled={!huboCambios || saving || saveOk}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ minWidth: 180 }}
        >
          {saving ? "Aplicando…" : saveOk ? "¡Aplicado!" : "Aplicar"}
        </Button>
      </DialogActions>
      {Toast}
    </Dialog>
  );
}
